'use client';

import React, { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { trackCTAClick } from '@/lib/analytics';

const steps = [
  {
    num: '01',
    icon: 'MagnifyingGlassIcon' as const,
    title: 'Mapowanie workflow',
    body: 'Dokumentujemy wszystkie reguły automatyzacji, integracje i niestandardowe konfiguracje w BaseLinker. Każdy workflow dostaje swój odpowiednik w Apilo.',
    detail: 'Wynik: pełna mapa migracji',
  },
  {
    num: '02',
    icon: 'ArrowsRightLeftIcon' as const,
    title: 'Synchronizacja danych',
    body: 'Przenosimy produkty, stany magazynowe, historię zamówień i ustawienia kont sprzedażowych. Dane są weryfikowane po każdym etapie.',
    detail: 'Zero utraty danych',
  },
  {
    num: '03',
    icon: 'BeakerIcon' as const,
    title: 'Testy równoległe',
    body: 'Przez 7–14 dni oba systemy działają równolegle. Porównujemy wyniki automatyzacji i eliminujemy rozbieżności przed finalnym przełączeniem.',
    detail: '7–14 dni testów',
  },
  {
    num: '04',
    icon: 'BoltIcon' as const,
    title: 'Przełączenie produkcyjne',
    body: 'Planujemy cutover w oknie niskiego ruchu. Monitoring przez pierwsze 48h — jesteśmy dostępni na bieżąco i reagujemy natychmiast.',
    detail: 'Wsparcie 48h po starcie',
  },
];

interface MigracjaContent {
  title: string;
  subtitle: string;
}

export default function MigracjaSection() {
  const [content, setContent] = useState<MigracjaContent>({
    title: 'Przenosimy Cię bez przestojów.',
    subtitle: 'Migracja z BaseLinker do Apilo to nie kopiuj-wklej. To przeprojektowanie procesów pod nową architekturę.',
  });
  const supabase = createClient();

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const { data, error } = await supabase
          .from('settings')
          .select('key, value')
          .in('key', ['migracja_title', 'migracja_subtitle']);

        if (error) {
          console.error('Error fetching migracja content:', error.message);
          return;
        }

        const settingsMap: Record<string, string> = {};
        data?.forEach((setting) => {
          settingsMap[setting.key] = setting.value;
        });

        setContent({
          title: settingsMap['migracja_title'] || content.title,
          subtitle: settingsMap['migracja_subtitle'] || content.subtitle,
        });
      } catch (err) {
        console.error('Error:', err);
      }
    };

    fetchContent();
  }, [supabase]);

  return (
    <section id="migracja" className="py-14 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="mb-14 grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-6 space-y-3">
            <p className="text-xs font-700 uppercase tracking-wide text-blue-DEFAULT" style={{ fontFamily: 'Inter, sans-serif' }}>Migracja BaseLinker → Apilo</p>
            <h2 className="text-4xl md:text-5xl font-700 tracking-tight text-navy leading-[1.0]" style={{ fontFamily: 'Inter, sans-serif' }}>
              {content.title}
            </h2>
          </div>
          <div className="lg:col-span-6">
            <p className="text-base text-muted font-400 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
              {content.subtitle}
            </p>
          </div>
        </div>

        {/* Steps grid — 2x2 asymmetric */}
        <div className="grid md:grid-cols-2 gap-4 mb-14">
          {steps.map((step) => (
            <div key={step.num} className="migration-step">
              {/* Big number background */}
              <div className="process-step-number" style={{ fontFamily: 'Inter, sans-serif' }}>{step.num}</div>

              <div className="relative z-10 space-y-3 pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-pale">
                    <Icon name={step.icon} size={18} className="text-blue-DEFAULT" variant="outline" />
                  </div>
                  <h3 className="text-lg font-700 text-navy tracking-tight" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {step.title}
                  </h3>
                </div>
                <p className="text-sm text-muted font-400 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {step.body}
                </p>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-pale rounded-full">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-DEFAULT" />
                  <span className="text-xs font-700 text-blue-DEFAULT uppercase tracking-wide" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {step.detail}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA after migration section */}
        <div className="bg-navy rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-2 text-center md:text-left">
            <h3 className="text-2xl font-700 text-white tracking-tight" style={{ fontFamily: 'Inter, sans-serif' }}>
              Gotowy na migrację do Apilo?
            </h3>
            <p className="text-sm text-white/50 font-400" style={{ fontFamily: 'Inter, sans-serif' }}>
              Bezpłatna analiza Twojego BaseLinker — bez zobowiązań.
            </p>
          </div>
          <a
            href="#kontakt"
            className="inline-flex items-center justify-center gap-2 bg-blue-DEFAULT text-white font-800 text-sm px-6 rounded-xl transition-all duration-300 ease-out hover:bg-blue-light hover:scale-105 hover:shadow-lg active:scale-95 h-11"
            style={{ fontFamily: 'Inter, sans-serif' }}
            onClick={() => trackCTAClick('Wycena migracji', 'migracja_section')}
          >
            Zamów wycenę migracji
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}